import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { OnboardingState, GarageDetailResponse } from '../types';

const STORAGE_KEY = 'garagat_onboarding_draft';
const TOTAL_STEPS = 3;

const initialState: OnboardingState = {
  garageId: null,
  step: 1,
  tradeLicense: { licenseNumber: '', files: [] },
  ownerEmiratesId: { files: [] },
  garageInfo: {
    name: '',
    phone: '',
    email: '',
    city: '',
    area: '',
    streetAddress: '',
    managerName: '',
    dailyCapacity: null,
    numberOfWorkers: null,
    latitude: null,
    longitude: null,
  },
  vatDeclaration: {
    vatRegistered: null,
    trn: '',
    vatRegisteredFrom: '',
  },
  isDirty: false,
};

type OnboardingAction =
  | { type: 'SET_GARAGE_ID'; garageId: string }
  | { type: 'SET_STEP'; step: number }
  | { type: 'SET_TRADE_LICENSE'; data: OnboardingState['tradeLicense'] }
  | { type: 'SET_OWNER_EMIRATES_ID'; data: OnboardingState['ownerEmiratesId'] }
  | { type: 'SET_GARAGE_INFO'; data: Partial<OnboardingState['garageInfo']> }
  | { type: 'SET_VAT_DECLARATION'; data: Partial<OnboardingState['vatDeclaration']> }
  | { type: 'RESET' }
  | { type: 'LOAD'; state: OnboardingState }
  | { type: 'LOAD_FROM_BACKEND'; detail: GarageDetailResponse };

function reducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'SET_GARAGE_ID':
      return { ...state, garageId: action.garageId };
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'SET_TRADE_LICENSE':
      return { ...state, tradeLicense: action.data, isDirty: true };
    case 'SET_OWNER_EMIRATES_ID':
      return { ...state, ownerEmiratesId: action.data, isDirty: true };
    case 'SET_GARAGE_INFO':
      return { ...state, garageInfo: { ...state.garageInfo, ...action.data }, isDirty: true };
    case 'SET_VAT_DECLARATION':
      return { ...state, vatDeclaration: { ...state.vatDeclaration, ...action.data }, isDirty: true };
    case 'RESET':
      localStorage.removeItem(STORAGE_KEY);
      return { ...initialState, isDirty: false };
    case 'LOAD':
      return { ...action.state, isDirty: false };
    case 'LOAD_FROM_BACKEND': {
      const d = action.detail;
      const isPlaceholder = (val: string | null | undefined, placeholder: string) =>
        !val || val === placeholder;

      // Map media by type
      const tradeLicenseFiles = d.media
        .filter((m) => m.mediaType === 'TRADE_LICENSE')
        .map((m) => ({ mediaId: m.id, storageKey: m.storageKey, fileName: m.originalFilename, downloadUrl: m.downloadUrl }));

      // Map backend step to 3-step model, clamping to max TOTAL_STEPS
      const mapBackendStep = (backendStep: number): number => {
        if (backendStep <= 2) return backendStep; // Steps 1-2 unchanged
        // Steps 3+ (old working-hours, services, terms) all map to step 3 (terms)
        return TOTAL_STEPS;
      };

      return {
        garageId: d.id,
        step: mapBackendStep(d.onboardingStep || 1),
        tradeLicense: {
          licenseNumber: d.licenseNumber || '',
          files: tradeLicenseFiles,
        },
        // Owner Emirates ID lives in garage_documents (not garage_media), so it isn't part
        // of the GarageDetailResponse. Preserve whatever's already in state (set by
        // LicenseAndIdStep's listMyDocuments hydration) so this action doesn't race-wipe it.
        ownerEmiratesId: state.ownerEmiratesId,
        garageInfo: {
          name: isPlaceholder(d.name, 'Draft') ? '' : d.name,
          phone: isPlaceholder(d.phone, 'draft') ? '' : d.phone,
          email: d.email || '',
          city: d.city || '',
          area: d.area || '',
          streetAddress: d.streetAddress || '',
          managerName: d.managerName || '',
          dailyCapacity: d.dailyCapacity ?? null,
          numberOfWorkers: d.numberOfWorkers ?? null,
          latitude: d.latitude ?? null,
          longitude: d.longitude ?? null,
        },
        // The DB column vat_registered defaults to FALSE, so only treat the answer
        // as "declared" when vatStatusDeclaredAt is set (or the draft carries an
        // affirmative signal). Otherwise the question stays unanswered (null).
        vatDeclaration: {
          vatRegistered:
            d.vatStatusDeclaredAt != null
              ? (d.vatRegistered ?? false)
              : d.vatRegistered === true
                ? true
                : state.vatDeclaration.vatRegistered,
          trn: d.trn ?? state.vatDeclaration.trn,
          vatRegisteredFrom: d.vatRegisteredFrom ?? state.vatDeclaration.vatRegisteredFrom,
        },
        isDirty: false,
      };
    }
    default:
      return state;
  }
}

const OnboardingStateContext = createContext<OnboardingState | null>(null);
const OnboardingDispatchContext = createContext<React.Dispatch<OnboardingAction> | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as OnboardingState;
        // Clamp step to new 3-step model for old drafts
        const step = parsed.step > TOTAL_STEPS ? TOTAL_STEPS : parsed.step;
        return { ...initialState, ...parsed, step, isDirty: false };
      } catch {
        return initialState;
      }
    }
    return initialState;
  });

  // Auto-save to localStorage on every state change
  useEffect(() => {
    if (state.isDirty) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  return (
    <OnboardingStateContext.Provider value={state}>
      <OnboardingDispatchContext.Provider value={dispatch}>
        {children}
      </OnboardingDispatchContext.Provider>
    </OnboardingStateContext.Provider>
  );
}

export function useOnboardingState() {
  const ctx = useContext(OnboardingStateContext);
  if (!ctx) throw new Error('useOnboardingState must be used within OnboardingProvider');
  return ctx;
}

export function useOnboardingDispatch() {
  const ctx = useContext(OnboardingDispatchContext);
  if (!ctx) throw new Error('useOnboardingDispatch must be used within OnboardingProvider');
  return ctx;
}

export function clearOnboardingDraft() {
  localStorage.removeItem(STORAGE_KEY);
}
